<template>
  <section class="company-overview">
    <div class="company-overview__grid">
      <article class="company-overview__card company-overview__card--hero">
        <div class="company-overview__card-head company-overview__card-head--hero">
          <div>
            <p class="company-overview__eyebrow">Overview</p>
            <h2 class="company-overview__title">Estado general del cliente</h2>
          </div>

          <button class="company-overview__link-action" type="button" @click="$emit('edit-record')">
            Editar ficha
          </button>
        </div>

        <div class="company-overview__hero-body">
          <div class="company-overview__hero-summary">
            <div class="company-overview__health-badge" :class="healthClass">
              {{ health.label }}
            </div>
            <p class="company-overview__hero-copy">
              {{ pendingItems.length ? 'Hay frentes operativos por completar en la ficha.' : 'No hay alertas operativas activas.' }}
            </p>
          </div>

          <p v-if="showLegalFiscalNotApplicableNotice" class="company-overview__notice">
            Los atributos legales y fiscales no aplican a este cliente en el cálculo de ficha completa.
          </p>

          <div v-if="pendingItems.length" class="company-overview__pending-inline">
            <span class="company-overview__pending-label">Pendientes</span>
            <ul class="company-overview__pending-list">
              <li v-for="item in pendingItems" :key="item">
                {{ item }}
              </li>
            </ul>
          </div>
        </div>
      </article>

      <article class="company-overview__card company-overview__card--metrics">
        <div class="company-overview__card-head company-overview__card-head--compact">
          <div>
            <p class="company-overview__eyebrow">Indicadores</p>
            <h3 class="company-overview__section-title">Datos clave</h3>
          </div>
        </div>

        <div class="company-overview__metrics">
          <div class="company-overview__metric">
            <span class="company-overview__metric-label">REMU</span>
            <strong class="company-overview__metric-value">
              {{ record.id_remu || 'Sin información' }}
            </strong>
          </div>
          <div class="company-overview__metric">
            <span class="company-overview__metric-label">CONTA</span>
            <strong class="company-overview__metric-value">
              {{ record.id_conta || 'Sin información' }}
            </strong>
          </div>
          <div class="company-overview__metric">
            <span class="company-overview__metric-label">Contactos</span>
            <strong class="company-overview__metric-value">{{ stats.contacts }}</strong>
          </div>
          <div class="company-overview__metric">
            <span class="company-overview__metric-label">Secretos</span>
            <strong class="company-overview__metric-value">{{ stats.secrets }}</strong>
          </div>
        </div>
      </article>

      <article class="company-overview__card company-overview__card--facts">
        <div class="company-overview__card-head company-overview__card-head--compact">
          <div>
            <p class="company-overview__eyebrow">Ficha</p>
            <h3 class="company-overview__section-title">{{ legalFiscalSectionTitle }}</h3>
          </div>

          <button class="company-overview__text-button" type="button" @click="$emit('edit-record')">
            Abrir ficha
          </button>
        </div>

        <p v-if="showLegalFiscalNotApplicableNotice" class="company-overview__facts-copy">
          Estos datos no participan en la ficha completa. Se muestran solo como referencia operativa.
        </p>

        <div class="company-overview__facts" :class="{ 'company-overview__facts--reference': showLegalFiscalNotApplicableNotice }">
          <div class="company-overview__fact">
            <span class="company-overview__fact-label">Inicio de actividades</span>
            <span class="company-overview__fact-value">{{ formattedStartActivities }}</span>
          </div>
          <div class="company-overview__fact">
            <span class="company-overview__fact-label">Régimen tributario</span>
            <span class="company-overview__fact-value">{{ record.tax_regime_name || 'Sin información' }}</span>
          </div>
          <div class="company-overview__fact">
            <span class="company-overview__fact-label">Tipo facturador</span>
            <span class="company-overview__fact-value">{{ record.biller_type_name || 'Sin información' }}</span>
          </div>
          <div class="company-overview__fact">
            <span class="company-overview__fact-label">Tipo organizacional</span>
            <span class="company-overview__fact-value">{{ record.organizational_type || 'Sin información' }}</span>
          </div>
        </div>
      </article>

      <article class="company-overview__card company-overview__card--activity">
        <div class="company-overview__card-head company-overview__card-head--compact">
          <div>
            <p class="company-overview__eyebrow">Actividad</p>
            <h3 class="company-overview__section-title">Movimiento reciente</h3>
          </div>

          <button class="company-overview__text-button" type="button" @click="$emit('view-activity-history')">
            Ver historial
          </button>
        </div>

        <div v-if="activity.length" class="company-overview__activity-list">
          <div v-for="entry in activity" :key="entry.id || `${entry.title || entry.label}-${entry.created_at || ''}`" class="company-overview__activity-item">
            <div class="company-overview__activity-copy">
              <p class="company-overview__activity-label">{{ entry.title || entry.label }}</p>
              <p v-if="entry.meta" class="company-overview__activity-meta">{{ entry.meta }}</p>
            </div>
            <p v-if="entry.created_at" class="company-overview__activity-date">{{ formatDateTime(entry.created_at) }}</p>
          </div>
        </div>

        <div v-else class="company-overview__empty-state">
          No hay actividad historica registrada aun. Los movimientos comenzaran a verse desde esta implementacion.
        </div>
      </article>

      <article class="company-overview__card company-overview__card--shortcuts">
        <div class="company-overview__card-head company-overview__card-head--compact">
          <div>
            <p class="company-overview__eyebrow">Atajos</p>
            <h3 class="company-overview__section-title">Ir a modulos</h3>
          </div>
        </div>

        <div class="company-overview__shortcuts">
          <button class="company-overview__shortcut" type="button" @click="$emit('open-module', 'people')">
            <span>Personas</span>
            <span class="company-overview__shortcut-arrow">→</span>
          </button>
          <button class="company-overview__shortcut" type="button" @click="$emit('open-module', 'operations')">
            <span>Operación</span>
            <span class="company-overview__shortcut-arrow">→</span>
          </button>
          <button class="company-overview__shortcut" type="button" @click="$emit('open-module', 'legal')">
            <span>Legal y Fiscal</span>
            <span class="company-overview__shortcut-arrow">→</span>
          </button>
          <button class="company-overview__shortcut" type="button" @click="$emit('open-module', 'access')">
            <span>Accesos</span>
            <span class="company-overview__shortcut-arrow">→</span>
          </button>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import { formatDateOnly } from '@/utils/date'

export default {
  name: 'CompanyOverviewPanel',
  props: {
    company: {
      type: Object,
      required: true
    },
    record: {
      type: Object,
      required: true
    },
    stats: {
      type: Object,
      required: true
    },
    health: {
      type: Object,
      required: true
    },
    activity: {
      type: Array,
      default: () => []
    },
    pendingItems: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    formattedStartActivities () {
      return formatDateOnly(this.record.start_activities)
    },
    showLegalFiscalNotApplicableNotice () {
      return this.company && this.company.client_type === 'persona_natural' && this.record && this.record.requires_legal_fiscal_info === false
    },
    legalFiscalSectionTitle () {
      return this.showLegalFiscalNotApplicableNotice ? 'Información legal y fiscal referencial' : 'Resumen tributario y legal'
    },
    healthClass () {
      const tone = this.health && this.health.tone ? this.health.tone : 'neutral'
      return `company-overview__health-badge--${tone}`
    }
  },
  methods: {
    formatDate (value) {
      return value ? new Date(value).toLocaleDateString() : ''
    },
    formatDateTime (value) {
      return value ? new Date(value).toLocaleString() : ''
    }
  }
}
</script>

<style scoped>
.company-overview__grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 0.95rem;
}

.company-overview__card {
  padding: 0.95rem 1rem;
  border: 1px solid rgba(110, 91, 66, 0.08);
  border-radius: 26px;
  background: linear-gradient(180deg, rgba(255, 252, 247, 0.92), rgba(246, 239, 231, 0.86));
}

.company-overview__card--hero {
  grid-column: span 7;
}

.company-overview__card--metrics,
.company-overview__card--facts,
.company-overview__card--shortcuts {
  grid-column: span 5;
}

.company-overview__card--activity {
  grid-column: span 7;
}

.company-overview__card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.85rem;
}

.company-overview__card-head--compact {
  margin-bottom: 0.72rem;
}

.company-overview__card-head--hero {
  margin-bottom: 0.75rem;
}

.company-overview__eyebrow {
  margin: 0 0 0.3rem;
  font-size: 0.68rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(58, 46, 33, 0.46);
}

.company-overview__title,
.company-overview__section-title {
  margin: 0;
  color: #211913;
}

.company-overview__title {
  font-size: 1.12rem;
}

.company-overview__section-title {
  font-size: 0.95rem;
}

.company-overview__link-action,
.company-overview__text-button {
  border: 0;
  background: transparent;
  color: #6a543d;
  font-weight: 600;
}

.company-overview__hero-body {
  display: grid;
  gap: 0.8rem;
}

.company-overview__hero-summary {
  display: grid;
  gap: 0.6rem;
}

.company-overview__hero-copy {
  margin: 0;
  color: rgba(45, 35, 26, 0.62);
  line-height: 1.38;
}

.company-overview__notice {
  margin: 0;
  padding: 0.75rem 0.9rem;
  border-radius: 18px;
  background: rgba(184, 139, 59, 0.12);
  color: #775227;
  font-size: 0.9rem;
  line-height: 1.5;
}

.company-overview__health-badge {
  display: inline-flex;
  align-items: center;
  min-height: 2rem;
  width: fit-content;
  padding: 0.36rem 0.74rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
}

.company-overview__health-badge--good {
  background: rgba(77, 140, 97, 0.12);
  color: #2f6a43;
}

.company-overview__health-badge--warning {
  background: rgba(184, 139, 59, 0.12);
  color: #8a6520;
}

.company-overview__health-badge--critical {
  background: rgba(168, 78, 78, 0.1);
  color: #8a3f3f;
}

.company-overview__health-badge--neutral {
  background: rgba(95, 107, 122, 0.12);
  color: #5a6574;
}

.company-overview__pending-inline {
  display: grid;
  gap: 0.4rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(95, 76, 52, 0.08);
}

.company-overview__pending-label {
  color: rgba(58, 46, 33, 0.48);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.company-overview__pending-list {
  display: grid;
  gap: 0.28rem;
  margin: 0;
  padding-left: 1rem;
  color: #5a4a3b;
}

.company-overview__metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.55rem;
}

.company-overview__metric {
  padding: 0.7rem 0.76rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.3);
}

.company-overview__metric-label {
  display: block;
  margin-bottom: 0.18rem;
  color: rgba(58, 46, 33, 0.5);
  font-size: 0.66rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.company-overview__metric-value {
  color: #241c16;
  font-size: 0.98rem;
}

.company-overview__facts {
  display: grid;
  gap: 0.55rem;
}

.company-overview__facts-copy {
  margin: 0 0 0.7rem;
  color: rgba(88, 67, 39, 0.76);
  font-size: 0.88rem;
  line-height: 1.5;
}

.company-overview__facts--reference .company-overview__fact {
  background: rgba(255, 255, 255, 0.18);
  border: 1px dashed rgba(161, 125, 72, 0.28);
}

.company-overview__facts--reference .company-overview__fact-value {
  color: rgba(36, 28, 22, 0.76);
}

.company-overview__fact {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-bottom: 0.48rem;
  border-bottom: 1px solid rgba(95, 76, 52, 0.08);
}

.company-overview__fact:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.company-overview__fact-label {
  color: rgba(58, 46, 33, 0.56);
}

.company-overview__fact-value {
  color: #241c16;
  font-weight: 600;
  text-align: right;
}

.company-overview__activity-list {
  display: grid;
  gap: 0;
}

.company-overview__activity-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.72rem 0;
  border-bottom: 1px solid rgba(95, 76, 52, 0.08);
}

.company-overview__activity-item:first-child {
  padding-top: 0.08rem;
}

.company-overview__activity-item:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.company-overview__activity-copy {
  min-width: 0;
}

.company-overview__activity-label {
  margin: 0;
  color: #241c16;
  font-weight: 600;
  line-height: 1.32;
}

.company-overview__activity-meta {
  margin: 0.14rem 0 0;
  color: rgba(45, 35, 26, 0.58);
  font-size: 0.84rem;
}

.company-overview__activity-date {
  margin: 0;
  color: rgba(45, 35, 26, 0.5);
  font-size: 0.8rem;
  white-space: nowrap;
}

.company-overview__shortcuts {
  display: grid;
  gap: 0.4rem;
}

.company-overview__shortcut {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 2.45rem;
  padding: 0 0.05rem;
  border: 0;
  border-bottom: 1px solid rgba(95, 76, 52, 0.08);
  background: transparent;
  color: #392d22;
  font-weight: 600;
  text-align: left;
}

.company-overview__shortcut:last-child {
  border-bottom: 0;
}

.company-overview__shortcut-arrow {
  color: rgba(58, 46, 33, 0.4);
  font-size: 0.95rem;
}

.company-overview__empty-state {
  color: rgba(45, 35, 26, 0.58);
}

@media (min-width: 1600px) and (max-width: 1799px) {
  .company-overview__grid {
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 0.9rem;
  }

  .company-overview__card {
    padding: 0.92rem 0.96rem;
  }

  .company-overview__card--hero {
    grid-column: span 7;
  }

  .company-overview__card--metrics {
    grid-column: span 5;
  }

  .company-overview__card--facts {
    grid-column: span 5;
  }

  .company-overview__card--activity {
    grid-column: span 5;
  }

  .company-overview__card--shortcuts {
    grid-column: span 2;
  }

  .company-overview__hero-body {
    grid-template-columns: minmax(0, 1fr) minmax(0, 0.95fr);
    gap: 0.85rem;
  }

  .company-overview__pending-inline {
    padding-top: 0;
    padding-left: 0.8rem;
    border-top: 0;
    border-left: 1px solid rgba(95, 76, 52, 0.08);
  }

  .company-overview__activity-item {
    padding: 0.68rem 0;
  }

  .company-overview__shortcut {
    min-height: 2.24rem;
  }
}

@media (min-width: 1800px) {
  .company-overview__grid {
    grid-template-columns: repeat(14, minmax(0, 1fr));
    gap: 0.85rem;
  }

  .company-overview__card {
    padding: 0.88rem 0.94rem;
  }

  .company-overview__card--hero {
    grid-column: span 8;
  }

  .company-overview__card--metrics {
    grid-column: span 6;
  }

  .company-overview__card--facts {
    grid-column: span 5;
  }

  .company-overview__card--activity {
    grid-column: span 6;
  }

  .company-overview__card--shortcuts {
    grid-column: span 3;
  }

  .company-overview__hero-body {
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
    align-items: start;
    gap: 0.95rem;
  }

  .company-overview__pending-inline {
    padding-top: 0;
    padding-left: 0.9rem;
    border-top: 0;
    border-left: 1px solid rgba(95, 76, 52, 0.08);
  }

  .company-overview__activity-item {
    padding: 0.64rem 0;
  }

  .company-overview__shortcut {
    min-height: 2.3rem;
  }
}

@media (max-width: 1080px) {
  .company-overview__card,
  .company-overview__card--hero,
  .company-overview__card--metrics,
  .company-overview__card--facts,
  .company-overview__card--activity,
  .company-overview__card--shortcuts {
    grid-column: span 12;
  }
}

@media (max-width: 640px) {
  .company-overview__metrics {
    grid-template-columns: 1fr;
  }

  .company-overview__fact,
  .company-overview__activity-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .company-overview__fact-value {
    text-align: left;
  }
}
</style>


